import { useEffect, useRef, useState, useCallback } from "react";

interface UseInfiniteScrollOptions<T> {
  fetchFn: (page: number) => Promise<T[]>;
  pageSize?: number;
  threshold?: number;
  enabled?: boolean;
  deps?: any[];
}

export const useInfiniteScroll = <T>({
  fetchFn,
  pageSize = 10,
  threshold = 1.0,
  enabled = true,
  deps = [],
}: UseInfiniteScrollOptions<T>) => {
  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const loaderRef = useRef<HTMLDivElement | null>(null);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore || !enabled) return;
    setLoading(true);
    try {
      const newItems = await fetchFn(page);
      setData((prev) => (page === 1 ? newItems : [...prev, ...newItems]));
      if (newItems.length < pageSize) setHasMore(false);
      setPage((prev) => prev + 1);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [fetchFn, page, pageSize, hasMore, loading, enabled]);

  // ✅ deps dəyişəndə sıfırla və ilk səhifəni dərhal yüklə
  useEffect(() => {
    if (!enabled) return;
    setData([]);
    setPage(1);
    setHasMore(true);

    (async () => {
      setLoading(true);
      try {
        const newItems = await fetchFn(1);
        setData(newItems);
        setHasMore(newItems.length >= pageSize);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [enabled, ...deps]);

  // ✅ İndi burada artıq 1-ci səhifə üçün təkrar çağırmırıq
  useEffect(() => {
    if (!enabled) return;
    if (page === 1) return; // ⚡ 1-ci səhifə artıq yüklənib
    loadMore();
  }, [page]);

  useEffect(() => {
    if (!enabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !loading) {
          setPage((prev) => prev + 1);
        }
      },
      { root: null, rootMargin: "0px", threshold },
    );

    const node = loaderRef.current;
    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
    };
  }, [hasMore, loading, enabled, threshold]);

  const reset = useCallback(() => {
    setData([]);
    setPage(1);
    setHasMore(true);
  }, []);

  return { data, loading, error, hasMore, loaderRef, reset };
};
